import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { push, ref, remove, runTransaction, update } from "firebase/database";
import { auth, database } from "../constants/firebase";
import { useUser } from "../contexts/UserContext";
import { uploadFileToBunny } from "../utils/utils";
import BunnyVideoEmbed from "./BunnyVideoEmbed";
import { dateValue, rows, useRecord } from "../utils/appData";
import {
  LuPlus,
  LuPlay,
  LuChevronLeft,
  LuChevronRight,
  LuTrash2,
} from "react-icons/lu";
import ModalSheet from "./ModalSheet";
import { safeUrl } from "../utils/appData";
import { State } from "./AppPage";
export default function ProfileExtras({ uid, owner = false }) {
  const { refreshUser, currentUser } = useUser();
  const profile = useRecord(uid ? `Users/${uid}` : null);
  const reviewState = useRecord(uid ? `Reviews/${uid}` : null);
  const [busy, setBusy] = useState(false);
  const [manage, setManage] = useState(false);
  const [playing, setPlaying] = useState(null);
  const [reviewTab, setReviewTab] = useState("all");
  const [allReviews, setAllReviews] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  useEffect(() => {
    setManage(false);
    setPlaying(null);
    setAllReviews(false);
    setError("");
  }, [uid]);
  const clips = rows(profile.data?.portfolio)
    .filter((p) => p.video)
    .sort(
      (a, b) =>
        (a.order ?? Infinity) - (b.order ?? Infinity) ||
        dateValue(a.createdAt) - dateValue(b.createdAt),
    );
  const reviews = rows(reviewState.data)
    .filter(
      (r) =>
        Number.isInteger(Number(r.rating)) &&
        Number(r.rating) >= 1 &&
        Number(r.rating) <= 5,
    )
    .sort((a, b) => dateValue(b.createdAt) - dateValue(a.createdAt));
  const shownReviews = reviews.filter(
    (r) =>
      reviewTab === "all" ||
      r.direction ===
      (reviewTab === "client" ? "creative_to_hirer" : "hirer_to_creative"),
  );

  const isOwnProfile = currentUser?.uid === uid;
  const profileUser = profile.data;
  const emptyBody = isOwnProfile
    ? 'Finish a job through zylen and the other side can rate you. Reviews only come from completed bookings.'
    : `${profileUser?.displayName || profileUser?.name || 'They'} has not finished a job through zylen yet.`;

  const avg = shownReviews.length
    ? shownReviews.reduce((n, r) => n + Number(r.rating), 0) /
    shownReviews.length
    : 0;
  async function upload(e, showreel) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("video/") || file.size > 500 * 1024 * 1024) {
      setError("Choose a video smaller than 500 MB.");
      return;
    }
    if (!showreel && clips.length >= 6) return;
    setBusy(true);
    setProgress(0);
    setError("");
    try {
      const result = await uploadFileToBunny(
        file,
        `${showreel ? "showreels" : "portfolio"}_${uid}`,
        file.name,
        setProgress,
      );
      if (!result.success || !result.playbackUrl)
        throw new Error(result.message || "Upload failed.");
      if (auth.currentUser?.uid !== uid)
        throw new Error("Your sign-in changed. Reload before saving.");
      if (showreel) {
        await update(ref(database, `Users/${uid}`), {
          video: result.playbackUrl,
          videoOrientation: result.videoOrientation || "landscape",
          videoWidth: result.videoWidth || null,
          videoHeight: result.videoHeight || null,
        });
        await refreshUser();
      } else {
        const id = push(ref(database, `Users/${uid}/portfolio`)).key;
        const saved = await runTransaction(
          ref(database, `Users/${uid}/portfolio`),
          (current) => {
            if (rows(current).filter((p) => p.video).length >= 6) return;
            return {
              ...current,
              [id]: {
                id,
                video: result.playbackUrl,
                thumb: "",
                title: file.name.replace(/\.[^.]+$/, ""),
                orientation: result.videoOrientation || "landscape",
                createdAt: Date.now(),
                order:
                  Math.max(-1, ...rows(current).map((p) => p.order ?? -1)) + 1,
              },
            };
          },
        );
        if (!saved.committed)
          throw new Error(
            "Your portfolio already contains six clips. Remove one before adding another.",
          );
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  async function deleteClip(id) {
    if (!window.confirm("Remove this clip from your portfolio?")) return;
    try {
      await remove(ref(database, `Users/${uid}/portfolio/${id}`));
    } catch (e) {
      setError(e.message);
    }
  }
  async function move(index, delta) {
    const next = [...clips];
    [next[index], next[index + delta]] = [next[index + delta], next[index]];
    try {
      const patch = {};
      next.forEach((p, i) => (patch[`${p.id}/order`] = i));
      await update(ref(database, `Users/${uid}/portfolio`), patch);
    } catch (e) {
      setError(e.message);
    }
  }
  const reviewFilters = (
    <div className="mb-4 flex gap-2 overflow-x-auto">
      {[
        ["client", "As a client"],
        ["creative", "As a creative"],
        ["all", "All reviews"],
      ].map(([key, label]) => (
        <button
          key={key}
          aria-pressed={reviewTab === key}
          onClick={() => setReviewTab(key)}
          className={`shrink-0 rounded-full px-3 py-2 text-xs font-semibold ${reviewTab === key ? "bg-[#327691] text-white" : "bg-white text-slate-500 dark:bg-[#23272c] dark:text-slate-300"}`}
        >
          {label}
        </button>
      ))}
    </div>
  );
  const reviewSummary = shownReviews.length > 0 && (
    <div className="mb-4 rounded-[20px] border border-black/5 bg-white p-4 dark:border-[#363e46] dark:bg-[#23272c]">
      <div className="mb-4 flex items-center gap-4">
        <strong className="text-[36px] font-extrabold">{avg.toFixed(1)}</strong>
        <div>
          <span
            className="text-lg tracking-wider"
            aria-label={`${avg.toFixed(1)} out of 5 stars`}
          >
            {"★".repeat(Math.round(avg))}
            {"☆".repeat(5 - Math.round(avg))}
          </span>
          <p className="text-xs text-slate-500">
            ({shownReviews.length} reviews)
          </p>
        </div>
      </div>
      <div className="space-y-2">
        {[5, 4, 3, 2, 1].map((star) => {
          const count = shownReviews.filter(
            (r) => Number(r.rating) === star,
          ).length;
          return (
            <div key={star} className="flex items-center gap-3 text-xs">
              <span className="w-7">{star} ★</span>
              <div className="zy-review-bar h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
                <span
                  className="block h-full rounded-full bg-slate-800 dark:bg-slate-100"
                  style={{ width: `${(100 * count) / shownReviews.length}%` }}
                />
              </div>
              <span className="w-6 text-right text-slate-500">{count}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
  const reviewRow = (r) => (
    <article
      key={r.id}
      className="mb-3 rounded-2xl border border-black/5 bg-white p-4 dark:border-[#363e46] dark:bg-[#23272c]"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <Link
          className="text-sm font-bold"
          to={`/user-profile/${r.reviewerId}`}
        >
          {r.reviewerName || "Client"}
        </Link>
        <span aria-label={`${r.rating} out of 5 stars`} className="text-sm">
          {"★".repeat(Number(r.rating))}
        </span>
      </div>
      <p className="mt-2 whitespace-pre-wrap break-words text-[13px] leading-5">
        {r.comment}
      </p>
      <p className="mt-2 text-xs text-slate-500">{r.jobTitle}</p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {Array.isArray(r.tags) &&
          r.tags.map((tag) => (
            <span
              key={tag}
              className="rounded-md bg-slate-100 px-2 py-1 text-[10px] font-semibold text-slate-500 dark:bg-slate-800 dark:text-slate-300"
            >
              {tag}
            </span>
          ))}
      </div>
    </article>
  );
  const fileInput = (showreel) => (
    <input
      type="file"
      accept="video/*"
      className="sr-only"
      disabled={busy || (!showreel && clips.length >= 6)}
      onChange={(e) => upload(e, showreel)}
    />
  );
  return (
    <div className="text-slate-900 dark:text-slate-50">
      {(owner || profile.data?.video) && (
        <section className="mb-5">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-[17px] font-bold">Showreel</h2>
            {owner && profile.data?.video && (
              <label className="cursor-pointer rounded-full px-3 py-2 text-xs font-bold text-[#327691] dark:text-[#5FAFCB]">
                Replace{fileInput(true)}
              </label>
            )}
          </div>
          {profile.data?.video && !playing ? (
            <BunnyVideoEmbed
              videoUrl={profile.data.video}
              orientation={profile.data.videoOrientation}
              videoWidth={profile.data.videoWidth}
              videoHeight={profile.data.videoHeight}
              title="Showreel"
            />
          ) : owner && !profile.data?.video ? (
            <label
              className={`flex cursor-pointer flex-col items-center justify-center gap-3 rounded-[20px] border border-dashed border-slate-300 bg-white px-5 py-8 dark:border-slate-600 dark:bg-[#23272c] ${busy ? "opacity-60" : ""}`}
            >
              <span className="grid h-11 w-11 place-items-center rounded-full bg-[#edf3f5] text-[#327691] dark:bg-[#22333a] dark:text-[#5FAFCB]">
                <LuPlus size={22} />
              </span>
              <strong className="text-sm">Add showreel</strong>
              <small className="text-center text-xs text-slate-500">
                Upload your showreel to help you get work.
              </small>
              {fileInput(true)}
            </label>
          ) : null}
        </section>
      )}
      <section className="mb-5">
        <div className="mb-3 flex items-center justify-between gap-4">
          <h2 className="text-[17px] font-bold">
            Portfolio{" "}
            <span className="text-xs font-medium text-slate-500">
              {clips.length}/6
            </span>
          </h2>
          {owner && (
            <button
              onClick={() => setManage((v) => !v)}
              className="text-xs font-bold text-[#327691] dark:text-[#5FAFCB]"
            >
              {manage ? "Done" : clips.length ? "Manage" : "Add work"}
            </button>
          )}
        </div>
        {busy && (
          <p role="status" className="mb-3 text-sm">
            Uploading {Math.round(progress)}%
          </p>
        )}
        {error && (
          <p
            role="alert"
            className="mb-3 rounded-xl bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950 dark:text-red-200"
          >
            {error}
          </p>
        )}
        <State loading={profile.loading} error={profile.error} />
        <div className="grid grid-cols-2 gap-2.5 md:grid-cols-3">
          {clips.map((clip, i) => (
            <div key={clip.id} className="min-w-0">
              <button
                onClick={() => setPlaying(clip)}
                className="relative grid aspect-video w-full place-items-center overflow-hidden rounded-[14px] border border-black/5 bg-slate-200 dark:border-[#363e46] dark:bg-[#23272c]"
                aria-label={`Play ${clip.title || `portfolio clip ${i + 1}`}`}
              >
                {safeUrl(clip.thumb || clip.thumbnail) && (
                  <img
                    src={safeUrl(clip.thumb || clip.thumbnail)}
                    alt=""
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                )}
                <span className="relative grid h-10 w-10 place-items-center rounded-full bg-black/50 text-white">
                  <LuPlay size={19} />
                </span>
                <span className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/70 to-transparent px-2 pb-2 pt-5 text-left text-[11px] font-semibold text-white">
                  {clip.title || `Clip ${i + 1}`}
                </span>
              </button>
              {owner && manage && (
                <div className="mt-2">
                  <input
                    aria-label={`Title for clip ${i + 1}`}
                    defaultValue={clip.title}
                    maxLength={100}
                    className="w-full rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs dark:border-slate-600 dark:bg-[#23272c]"
                    onBlur={async (e) => {
                      try {
                        await update(
                          ref(database, `Users/${uid}/portfolio/${clip.id}`),
                          { title: e.target.value.trim() },
                        );
                      } catch (e) {
                        setError(e.message);
                      }
                    }}
                  />
                  <div className="mt-1 flex justify-between gap-2">
                    <button
                      disabled={i === 0 || busy}
                      onClick={() => move(i, -1)}
                      aria-label="Move clip earlier"
                      className="p-2 disabled:opacity-30"
                    >
                      <LuChevronLeft size={16} />
                    </button>
                    <button
                      disabled={i === clips.length - 1 || busy}
                      onClick={() => move(i, 1)}
                      aria-label="Move clip later"
                      className="p-2 disabled:opacity-30"
                    >
                      <LuChevronRight size={16} />
                    </button>
                    <button
                      onClick={() => deleteClip(clip.id)}
                      aria-label="Remove portfolio clip"
                      className="p-2 text-red-600"
                    >
                      <LuTrash2 size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {owner && clips.length < 6 && (
            <label className="grid aspect-video cursor-pointer place-items-center rounded-[14px] border border-dashed border-slate-300 bg-white text-[#327691] dark:border-slate-600 dark:bg-[#23272c] dark:text-[#5FAFCB]">
              <span className="flex flex-col items-center gap-2">
                <LuPlus size={22} />
                <span className="text-xs font-bold">Add clip</span>
              </span>
              {fileInput(false)}
            </label>
          )}
        </div>
        {!owner && !clips.length && !profile.loading && (
          <p className="rounded-2xl bg-white p-5 text-sm text-slate-500 dark:bg-[#23272c]">
            No portfolio clips yet.
          </p>
        )}
      </section>
      <section id="profile-reviews" className="scroll-mt-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-[17px] font-bold">Reviews</h2>
          {reviews.length > 3 && (
            <button
              onClick={() => setAllReviews(true)}
              className="text-xs font-bold text-[#327691] dark:text-[#5FAFCB]"
            >
              View all ({reviews.length})
            </button>
          )}
        </div>
        {reviews.length > 0 && reviewFilters}
        <State {...reviewState} />
        {reviewSummary}
        {shownReviews.slice(0, 3).map(reviewRow)}
        {!shownReviews.length && !reviewState.loading && (
          <div className="rounded-[20px] border border-black/5 bg-white p-5 dark:border-[#363e46] dark:bg-[#23272c]">
            <strong className="text-sm">No reviews yet.</strong>
            <p className="text-xs leading-5 text-slate-500">
              {emptyBody}
            </p>
          </div>
        )}
      </section>
      <ModalSheet
        open={Boolean(playing)}
        onClose={() => setPlaying(null)}
        title={playing?.title || "Portfolio clip"}
      >
        {playing && (
          <BunnyVideoEmbed
            videoUrl={playing.video}
            orientation={playing.orientation}
            title={playing.title || "Portfolio clip"}
          />
        )}
      </ModalSheet>
      <ModalSheet
        open={allReviews}
        onClose={() => setAllReviews(false)}
        title="Reviews"
      >
        {reviewFilters}
        {reviewSummary}
        {shownReviews.map(reviewRow)}
      </ModalSheet>
    </div>
  );
}
